import { Injectable, signal } from '@angular/core';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { downloadFile } from '../../utils/cta-button-actions/document-actions';

@Injectable({
  providedIn: 'root',
})
export class DownloadDocumentService {
  public dateRange = signal<{
    startDate: string | null;
    endDate: string | null;
  }>({ startDate: '', endDate: '' });

  constructor(private accountService: AccountService) {}

  // Bank statement download: either notify flutter with URL or fetch blob and trigger download in browser
  public downloadBankDocument(
    accountId: string,
    documentTemplateType: string,
    startDate: any,
    endDate: any,
    downloadFileType: string,
    flutterWebView?: any,
    currency?: string,
  ): Observable<null> {
    if (flutterWebView) {
      const url = this.accountService.bankDocumentDownloadUrl(
        accountId,
        documentTemplateType,
        startDate,
        endDate,
        downloadFileType,
        currency,
      );
      flutterWebView.callHandler('pdfDownloadHandler', [url]);
      return of(null);
    }

    return this.accountService
      .bankDocumentDownload(accountId, documentTemplateType, startDate, endDate, downloadFileType, currency)
      .pipe(
        take(1),
        map((response: unknown) => {
          this.downloadResponse(response, downloadFileType);
          return null;
        }),
        catchError((err) => {
          console.error('Error Downloading Document:', err);
          return of(null);
        }),
      );
  }

  public downloadSavingsBankDocument(
    accountId: string,
    documentTemplateType: string,
    startDate: any,
    endDate: any,
    downloadFileType: string,
    flutterWebView?: any,
  ): Observable<null> {
    if (flutterWebView) {
      const url = this.accountService.savingsBankDocumentDownloadUrl(
        accountId,
        documentTemplateType,
        startDate,
        endDate,
        downloadFileType,
      );
      flutterWebView.callHandler('pdfDownloadHandler', [url]);
      return of(null);
    }

    return this.accountService
      .savingsBankDocumentDownload(accountId, documentTemplateType, startDate, endDate, downloadFileType)
      .pipe(
        take(1),
        map((response: unknown) => {
          this.downloadResponse(response, downloadFileType);
          return null;
        }),
        catchError((err) => {
          console.error('Error Downloading Savings Bank Document:', err);
          return of(null);
        }),
      );
  }

  // Holding statement download: uses the dedicated holding-statements endpoint
  public downloadHoldingDocument(
    documentTemplateType: string,
    startDate: string,
    endDate: string,
    downloadFileType: string,
    flutterWebView?: any,
  ): Observable<null> {
    if (flutterWebView) {
      const url = this.accountService.holdingStatementDownloadUrl(
        documentTemplateType,
        startDate,
        endDate,
        downloadFileType,
      );
      flutterWebView.callHandler('pdfDownloadHandler', [url]);
      return of(null);
    }

    return this.accountService
      .holdingStatementDownload(documentTemplateType, startDate, endDate, downloadFileType)
      .pipe(
        take(1),
        map((response: unknown) => {
          this.downloadResponse(response, downloadFileType);
          return null;
        }),
        catchError((err) => {
          console.error('Error Downloading Holding Statement Document:', err);
          return of(null);
        }),
      );
  }

  // Payment proof download
  public downloadPaymentProof(
    accountId: string,
    documentTemplateType: string,
    transactionId: string,
    transactionType: string,
    downloadFileType: string,
    flutterWebView?: any,
  ): Observable<null> {
    if (flutterWebView) {
      const url = this.accountService.documentPaymentProofDownloadUrl(
        accountId,
        documentTemplateType,
        transactionId,
        transactionType,
        downloadFileType,
      );
      flutterWebView.callHandler('pdfDownloadHandler', [url]);
      return of(null);
    }

    return this.accountService
      .documentPaymentProofDownload(
        accountId,
        documentTemplateType,
        transactionId,
        transactionType,
        downloadFileType,
      )
      .pipe(
        take(1),
        map((response: unknown) => {
          this.downloadResponse(response, downloadFileType);
          return null;
        }),
        catchError((err) => {
          console.error('Error Downloading Payment Proof Document:', err);
          return of(null);
        }),
      );
  }

  // Exchange proof download
  public downloadExchangeProof(
    documentTemplateType: string,
    exchangeId: string,
    transactionType: string,
    downloadFileType: string,
    flutterWebView?: any,
  ): Observable<null> {
    if (flutterWebView) {
      const url = this.accountService.documentExchangeProofDownloadUrl(
        documentTemplateType,
        exchangeId,
        transactionType,
        downloadFileType,
      );
      flutterWebView.callHandler('pdfDownloadHandler', [url]);
      return of(null);
    }

    return this.accountService
      .documentExchangeProofDownload(documentTemplateType, exchangeId, transactionType, downloadFileType)
      .pipe(
        take(1),
        map((response: unknown) => {
          this.downloadResponse(response, downloadFileType);
          return null;
        }),
        catchError((err) => {
          console.error('Error Downloading Exchange Proof Document:', err);
          return of(null);
        }),
      );
  }

  public downloadFromDirectUrl(
    url: string,
    downloadFileType: string,
    flutterWebView?: unknown,
  ): Observable<null> {
    if (flutterWebView) {
      const documentUrl = this.accountService.keyProductInformationDownloadUrl(url);
      (flutterWebView as { callHandler: (name: string, args: unknown[]) => void }).callHandler(
        'pdfDownloadHandler',
        [documentUrl],
      );
      return of(null);
    }

    const documentUrl = this.accountService.keyProductInformationDownloadUrl(url);
    downloadFile(documentUrl, downloadFileType);
    return of(null);
  }

  // Utility to trigger browser download from response blob
  private downloadResponse(response: unknown, documentType: string) {
    try {
      const blob = new Blob([response as BlobPart], {
        type: `application/${documentType}`,
      });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = new Date().getTime() + `.${documentType}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Failed to process download response', err);
    }
  }

  public setDateRange(startDate: string | null, endDate: string | null) {
    this.dateRange.set({ startDate, endDate });
  }
}
